import {
  Box,
  IconButton,
  InputBase,
  List,
  ListItem,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Typography
} from '@material-ui/core';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import {
  ArrowDownwardRounded,
  ArrowUpwardRounded,
  SearchRounded
} from '@material-ui/icons';
import clsx from 'clsx';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

enum ORDER {
  desc = 'desc',
  asc = 'asc'
}

interface ParamProps {
  page?: number;
  limit?: number;
  sortBy?: string;
  orderBy?: ORDER.desc | ORDER.asc;
  search?: string | null;
}

interface sortOptionProps {
  value: string;
  text: string;
}

interface EnhancedTableToolbarProps<T> {
  name: string;
  params: T;
  sortOptions?: sortOptionProps[];
  loadingElement: (params: T) => void;
  canSearchable?: boolean;
  searchHint?: string;
}

export default function EnhancedTableToolbar<T extends ParamProps>({
  name,
  params,
  sortOptions,
  loadingElement,
  canSearchable = false,
  searchHint
}: EnhancedTableToolbarProps<T>) {
  const classes = useToolbarStyles();
  const { t } = useTranslation();
  
  const defaultSearchHint = searchHint || t('common.search');

  const [anchorElement, setAnchorElement] = useState<null | HTMLElement>(null);

  function handleOrder(event: React.MouseEvent<unknown>) {
    const isAsc = params.orderBy === ORDER.asc;
    loadingElement({ ...params, orderBy: isAsc ? ORDER.desc : ORDER.asc });
  }

  function handleClickListItem(event: React.MouseEvent<HTMLElement>) {
    setAnchorElement(event.currentTarget);
  }

  function handleMenuItemClick(
    event: React.MouseEvent<HTMLElement>,
    value: string
  ) {
    setAnchorElement(null);
    loadingElement({
      ...params,
      page: 0,
      sortBy: value,
      orderBy: value === 'id' ? ORDER.desc : ORDER.asc
    });
  }

  function handleClose() {
    setAnchorElement(null);
  }

  function handleChangeSearch(event: React.ChangeEvent<HTMLInputElement>) {
    loadingElement({
      ...params,
      page: 0,
      search: event.target.value === '' ? null : event.target.value
    });
    if (!event.target.value) {
      loadingElement({ ...params, page: 0, search: null });
    }
  }

  return (
    <div
      className={clsx(classes.projectHeader, {
        [classes.paddingSearchBox]: canSearchable
      })}
    >
      {canSearchable ? (
        <>
          <Paper className={classes.searchBox}>
            <SearchRounded color="action" />
            <InputBase
              className={classes.marginLeft}
              placeholder={defaultSearchHint as string}
              type="search"
              spellCheck={false}
              fullWidth
              value={params.search === null ? '' : params.search}
              onChange={handleChangeSearch}
              inputProps={{ 'aria-label': t('common.searchAriaLabel') as string }}
            />
          </Paper>
          <Box className={classes.flexGrow} />
        </>
      ) : (
        <Typography
          className={classes.flexGrow}
          variant="body1"
          color="textSecondary"
        >
          {name}
        </Typography>
      )}
      {sortOptions && (
        <>
          <List className={classes.paddingNone}>
            <ListItem
              className={classes.paddingNoneTB}
              button
              onClick={handleClickListItem}
            >
              <ListItemText
                primary={
                  sortOptions.find(
                    (sortOption) => sortOption.value === params.sortBy
                  )?.text
                }
              />
            </ListItem>
          </List>
          <Menu
            id="lock-menu"
            anchorEl={anchorElement}
            keepMounted
            open={Boolean(anchorElement)}
            onClose={handleClose}
          >
            {sortOptions.map((sortOption) => (
              <MenuItem
                key={sortOption?.value}
                selected={sortOption.value === params.sortBy}
                onClick={(event) =>
                  handleMenuItemClick(event, sortOption.value)
                }
              >
                {sortOption.text}
              </MenuItem>
            ))}
          </Menu>
          <IconButton size="small" onClick={handleOrder}>
            {params.orderBy === ORDER.asc ? (
              <ArrowUpwardRounded />
            ) : (
              <ArrowDownwardRounded />
            )}
          </IconButton>
        </>
      )}
    </div>
  );
}

const useToolbarStyles = makeStyles((theme: Theme) =>
  createStyles({
    projectHeader: {
      display: 'flex',
      alignItems: 'center',
      padding: theme.spacing(1)
    },
    searchBox: {
      display: 'flex',
      alignItems: 'center',
      flexGrow: 1,
      padding: theme.spacing(1),
      marginRight: theme.spacing(1),
      borderRadius: 18,
      height: 36,
      zIndex: 2,
      maxWidth: 300
    },
    flexGrow: {
      flexGrow: 1
    },
    marginLeft: {
      marginLeft: theme.spacing(1)
    },
    paddingSearchBox: {
      padding: theme.spacing(1, 1, 2, 0)
    },
    paddingNone: {
      padding: 0
    },
    paddingNoneTB: {
      paddingTop: 0,
      paddingBottom: 0
    }
  })
);
